/**
 * AI Prompts Configuration
 * 
 * This file contains configurable prompts for AI processing of documents.
 * Prompts can be easily modified by editing this file directly.
 * 
 * These prompts are used for:
 * - Document categorization
 * - Title generation
 * - Summary generation
 * - Preview generation
 */

import { DOCUMENT_TAXONOMY } from './documentTaxonomy';

export const AI_PROMPTS = {
  /**
   * Prompt for document categorization
   * Analyzes document content and classifies it into appropriate categories
   */
  categorization: `
    Analyze this document and classify it into one or more of the following categories:
    
    ${DOCUMENT_TAXONOMY.categories.map(cat =>
    `- ${cat.id}: ${cat.description}`
  ).join('\n')}
    
    Provide a confidence score for each category and a primary category.
    Explain your reasoning.
  `,

  /**
   * Prompt for title generation
   * Creates descriptive, user-friendly titles for documents
   */
  titleGeneration: `
    Generate a descriptive title for this document.
    Include key details like flight numbers, hotel names, locations, or important dates.
    Keep it concise but informative (under 60 characters).
    Avoid generic terms like "confirmation" or "booking" unless necessary.
    Focus on the most distinctive or important information in the document.
  `,

  /**
   * Prompt for summary generation
   * Creates brief summaries describing what the document is about
   */
  summaryGeneration: `
    Create a brief summary that describes what this document is about and its purpose.
    Focus on explaining:
    - What type of document this is (e.g., "Flight booking confirmation", "Hotel reservation", "Travel insurance policy")
    - What it's for or why someone would need it
    - The main service, product, or transaction it represents
    - Its relevance to travel planning
    
    Write in a descriptive, natural style that helps someone quickly understand the document's purpose.
    Keep it under 80 words and avoid listing specific data points like dates or reference numbers.
    Instead, focus on explaining what the document represents and why it matters.
    
    Example: "This is a hotel booking confirmation for a luxury resort stay. It confirms your accommodation reservation and includes details about the property amenities, check-in procedures, and cancellation policy."
  `,

  /**
   * Prompt for preview generation
   * Creates preview content for document display
   */
  previewGeneration: `
    Generate a preview of this document for display purposes.
    Extract the most important visual or textual elements that would help identify the document.
    
    For different document types:
    - Flight documents: Focus on airline, flight number, route, dates
    - Hotel documents: Focus on hotel name, location, dates, room details
    - Location documents: Focus on place name, address, key features
    - Planning documents: Focus on document type, key dates, important details
    
    Format the preview appropriately for the document type and content.
    Keep it concise but informative for quick identification.
  `,

  /**
   * Prompt for data extraction
   * Extracts structured data from documents
   */
  dataExtraction: `
    Extract structured data from this document.
    Look for and extract:
    - Dates and times
    - Locations and addresses
    - Names and contact information
    - Reference numbers (booking numbers, flight numbers, etc.)
    - Amounts and costs
    - Terms and conditions
    
    Only include fields that are clearly present in the document.
  `,

  /**
   * Prompt for flight-specific data extraction
   * Extracts data formatted for flight entity creation
   * @param {Object} tripContext - Optional trip context with startDate and endDate
   * @returns {string} The prompt text
   */
  flightExtraction: (tripContext = null) => {
    const basePrompt = `
    Extract flight booking information from this document.
    Look for and extract these specific fields:
    
    - airline: The airline name (e.g., "Qantas", "United Airlines")
    - flightNumber: The flight number (e.g., "QF21", "UA100")
    - departureCity: City of departure
    - departureAirport: Departure airport name or code
    - arrivalCity: City of arrival
    - arrivalAirport: Arrival airport name or code
    - departureTime: Departure date and time (ISO 8601 format: YYYY-MM-DDTHH:MM)
    - arrivalTime: Arrival date and time (ISO 8601 format: YYYY-MM-DDTHH:MM)
    - bookingReference: Booking/confirmation reference number
    - ticketNumbers: Array of ticket numbers
    - passengers: Array of passenger names
    - classOfService: Cabin class (economy, business, first)
    - baggageAllowance: Baggage details
    - seatNumbers: Assigned seats
    - cost: Total cost with currency
    
    Only include fields that are clearly present in the document.
    Use null for fields that cannot be determined.`;

    // Add trip context if available for better year inference
    if (tripContext?.startDate && tripContext?.endDate) {
      return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    This document is for a trip scheduled from ${tripContext.startDate} to ${tripContext.endDate}.
    When extracting dates:
    - If the year is visible in the document, use that year
    - If only day/month are visible (no year shown), use the year that places the date within this trip period
    `;
    }

    // Fallback when no trip dates available
    return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    If dates in this document do not show a year, assume they are for a future trip.
    Use the nearest future year that makes logical sense for travel bookings.
    `;
  },

  /**
   * Prompt for hotel/accommodation data extraction
   * Extracts data formatted for accommodation entity creation
   * @param {Object} tripContext - Optional trip context with startDate and endDate
   * @returns {string} The prompt text
   */
  hotelExtraction: (tripContext = null) => {
    const basePrompt = `
    Extract hotel/accommodation booking information from this document.
    Look for and extract these specific fields:
    
    - hotelName: Name of the hotel or property
    - address: Full address of the property
    - city: City where the property is located
    - country: Country where the property is located
    - checkInDate: Check-in date (ISO 8601 format: YYYY-MM-DD)
    - checkOutDate: Check-out date (ISO 8601 format: YYYY-MM-DD)
    - checkInTime: Check-in time
    - checkOutTime: Check-out time
    - roomType: Type of room booked
    - numberOfRooms: Number of rooms
    - numberOfGuests: Number of guests
    - guestNames: Array of guest names
    - bookingReference: Booking/confirmation reference number
    - cost: Total cost with currency
    - costPerNight: Cost per night with currency
    - amenities: Array of included amenities
    - cancellationPolicy: Cancellation policy details
    - contactPhone: Property phone number
    - contactEmail: Property email
    
    Only include fields that are clearly present in the document.
    Use null for fields that cannot be determined.`;

    // Add trip context if available for better year inference
    if (tripContext?.startDate && tripContext?.endDate) {
      return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    This document is for a trip scheduled from ${tripContext.startDate} to ${tripContext.endDate}.
    When extracting dates:
    - If the year is visible in the document, use that year
    - If only day/month are visible (no year shown), use the year that places the date within this trip period
    - Example: For a trip Dec 2025-Jan 2026, "Jan 12" should become "2026-01-12"
    `;
    }

    // Fallback when no trip dates available
    return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    If dates in this document do not show a year, assume they are for a future trip.
    Use the nearest future year that makes logical sense for travel bookings.
    `;
  },

  /**
   * Prompt for car rental data extraction
   * Extracts data formatted for car rental entity creation
   * @param {Object} tripContext - Optional trip context with startDate and endDate
   * @returns {string} The prompt text
   */
  carExtraction: (tripContext = null) => {
    const basePrompt = `
    Extract car rental booking information from this document.
    Look for and extract these specific fields:
    
    - rentalCompany: Name of the rental company (e.g., "Hertz", "Enterprise", "Avis")
    - bookingReference: Booking/confirmation reference number
    - pickupLocation: Pickup location (address or location name)
    - returnLocation: Return location (address or location name)
    - pickupTime: Pickup date and time (ISO 8601 format: YYYY-MM-DDTHH:MM)
    - returnTime: Return date and time (ISO 8601 format: YYYY-MM-DDTHH:MM)
    - vehicleClass: Type/class of vehicle (e.g., "Compact", "SUV", "Luxury")
    - vehicleModel: Specific vehicle model if mentioned
    - driverName: Primary driver name
    - additionalDrivers: Array of additional driver names
    - cost: Total cost with currency
    - costPerDay: Cost per day with currency
    - insuranceIncluded: Whether insurance is included (true/false)
    - mileagePolicy: Mileage policy (unlimited or limit details)
    - fuelPolicy: Fuel policy (full-to-full, prepaid, etc.)
    
    Only include fields that are clearly present in the document.
    Use null for fields that cannot be determined.`;

    // Add trip context if available for better year inference
    if (tripContext?.startDate && tripContext?.endDate) {
      return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    This document is for a trip scheduled from ${tripContext.startDate} to ${tripContext.endDate}.
    When extracting dates:
    - If the year is visible in the document, use that year
    - If only day/month are visible (no year shown), use the year that places the date within this trip period
    `;
    }

    // Fallback when no trip dates available
    return `${basePrompt}
    
    IMPORTANT - Date Year Inference:
    If dates in this document do not show a year, assume they are for a future trip.
    Use the nearest future year that makes logical sense for travel bookings.
    `;
  }
};


/**
 * Get a specific prompt by type
 * @param {string} promptType - The type of prompt to retrieve
 * @returns {string} The prompt text
 */
export const getPrompt = (promptType) => {
  return AI_PROMPTS[promptType] || '';
};

/**
 * Get all available prompt types
 * @returns {Array} Array of available prompt types
 */
export const getPromptTypes = () => {
  return Object.keys(AI_PROMPTS);
};